// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { TRAYS, WEIGHT_UNITS, utils } from '@seedplanner/shared-utils';
import Decimal from 'decimal.js';
import { getNumericID } from './getID';

interface CropInputData {
  readonly type: 'crop';
  readonly id: number;
  readonly cropName: string;
  readonly yieldPerTray: number;
  readonly yieldUnit: string;
  readonly harvestBufferTime: number;
  readonly totalGrowTime: number;
}

interface BlendInputData {
  readonly type: 'blend';
  readonly blendName: string;
  readonly crops: readonly (CropInputData & { readonly percentOfBlend: number })[];
}

export interface Input {
  readonly sizeAmount?: string;
  readonly sizeUnit: string;
  readonly quantity: string;
  readonly cropOrBlend: CropInputData | BlendInputData;
}

interface InputInput {
  size_amount?: string | null;
  quantity: string;
  unit: string;
  crop?: {
    id: string;
    crop_name: string;
    yield_per_tray: string;
    yield_unit: string;
    harvest_buffer_time: number;
    total_grow_time: number;
  } | null;
  blend?: {
    // id: string;
    blend_name: string;
    // status: string;
    crops: {
      edges: Array<{
        node: {
          id: string;
          percent_of_blend: number;
          crop?: {
            id: string;
            crop_name: string;
            yield_per_tray: string;
            yield_unit: string;
            harvest_buffer_time: number;
            total_grow_time: number;
          } | null;
        };
      }>;
    };
  } | null;
}

export function orderInputsToCalcInputs(inputs: readonly InputInput[]): readonly Input[] {
  return inputs
    .map((input) => {
      const sizeInfo = {
        sizeAmount: input.size_amount ?? undefined,
        sizeUnit: input.unit,
        quantity: input.quantity,
      };

      if (input.crop != null) {
        return {
          ...sizeInfo,
          cropOrBlend: {
            type: 'crop' as const,
            id: getNumericID(input.crop.id),
            cropName: input.crop.crop_name,
            yieldPerTray: Number.parseFloat(input.crop.yield_per_tray),
            yieldUnit: input.crop.yield_unit,
            harvestBufferTime: input.crop.harvest_buffer_time,
            totalGrowTime: input.crop.total_grow_time,
          },
        };
      }
      if (input.blend != null) {
        return {
          ...sizeInfo,
          cropOrBlend: {
            type: 'blend' as const,
            blendName: input.blend.blend_name,
            crops: input.blend.crops.edges
              .map(({ node }) => {
                const crop = node.crop;
                if (crop == null) {
                  return undefined;
                }
                return {
                  type: 'crop' as const,
                  id: getNumericID(crop.id),
                  cropName: crop.crop_name,
                  yieldPerTray: Number.parseFloat(crop.yield_per_tray),
                  yieldUnit: crop.yield_unit,
                  percentOfBlend: node.percent_of_blend / 100,
                  harvestBufferTime: crop.harvest_buffer_time,
                  totalGrowTime: crop.total_grow_time,
                };
              })
              .filter(utils.notNull),
          },
        };
      }

      return undefined;
    })
    .filter(utils.notNull);
}

interface PlantingData {
  readonly crop?: {
    readonly id: string;
  } | null;
  readonly yield_per_tray_ounces: string;
}

function getYieldsFromPlantings(plantings: readonly PlantingData[]): Map<number, number> {
  return plantings.reduce((acc, planting) => {
    if (planting.crop == null) {
      return acc;
    }
    const key = getNumericID(planting.crop.id);
    const maybeExisting = acc.get(key);
    if (maybeExisting === undefined) {
      acc.set(key, Number.parseFloat(planting.yield_per_tray_ounces));
    }
    return acc;
  }, new Map<number, number>());
}

export function calculateAmountsNeededFromInputsAndExistingPlantings(
  inputs: readonly Input[],
  existingPlantings: readonly PlantingData[],
): Map<number, { readonly cropData: CropDataWithExtra; readonly traysNeeded: number }> {
  const yieldsFromPlantings = getYieldsFromPlantings(existingPlantings);
  // Takes in all the inputs, and returns the amount needed for each crop to fulfill the inputs
  // Final amounts should be rounded to the nearest 1/100th of a tray
  const result = new Map<number, { readonly cropData: CropDataWithExtra; readonly traysNeeded: number }>();
  for (const input of inputs) {
    const quantity = Number.parseFloat(input.quantity);
    const sizeAmount = input.sizeAmount ? Number.parseFloat(input.sizeAmount) : 1;
    const amountOfInputNeededOunces = utils.convert({
      inputUnit: input.sizeUnit,
      outputUnit: WEIGHT_UNITS.OUNCES,
      quantity: Decimal.mul(quantity, sizeAmount).toNumber(),
    });

    // Convert amountOfInputNeededInOunces to a tray count based on the crop's yield
    // If there is an existing planting for this crop, then use that yield
    // To avoid inaccuracies when crop yield changes

    if (input.cropOrBlend.type === 'crop') {
      const crop = input.cropOrBlend;
      const maybeExistingYield = yieldsFromPlantings.get(crop.id);
      const yieldPerTrayOunces =
        maybeExistingYield ??
        utils.convert({
          inputUnit: crop.yieldUnit,
          outputUnit: WEIGHT_UNITS.OUNCES,
          quantity: crop.yieldPerTray,
        });
      const traysNeeded =
        input.sizeUnit === TRAYS
          ? quantity
          : utils.round(Decimal.div(amountOfInputNeededOunces, yieldPerTrayOunces).toNumber());
      const maybeExisting = result.get(crop.id);
      result.set(crop.id, {
        cropData: {
          id: crop.id,
          cropName: crop.cropName,
          totalGrowTime: crop.totalGrowTime,
          harvestBufferTime: crop.harvestBufferTime,
          yieldPerTray: crop.yieldPerTray,
          yieldUnit: crop.yieldUnit,
        },
        traysNeeded: utils.addTraysRaw(maybeExisting ? maybeExisting.traysNeeded : 0, traysNeeded),
      });
    } else {
      const blend = input.cropOrBlend;
      for (const crop of blend.crops) {
        const maybeExistingYield = yieldsFromPlantings.get(crop.id);
        const yieldPerTrayOunces =
          maybeExistingYield ??
          utils.convert({
            inputUnit: crop.yieldUnit,
            outputUnit: WEIGHT_UNITS.OUNCES,
            quantity: crop.yieldPerTray,
          });
        const traysNeeded = utils.round(
          Decimal.mul(amountOfInputNeededOunces, crop.percentOfBlend).div(yieldPerTrayOunces).toNumber(),
        );
        const maybeExisting = result.get(crop.id);
        result.set(crop.id, {
          cropData: {
            id: crop.id,
            cropName: crop.cropName,
            totalGrowTime: crop.totalGrowTime,
            harvestBufferTime: crop.harvestBufferTime,
            yieldPerTray: crop.yieldPerTray,
            yieldUnit: crop.yieldUnit,
          },
          traysNeeded: utils.addTraysRaw(maybeExisting ? maybeExisting.traysNeeded : 0, traysNeeded),
        });
      }
    }
  }

  for (const [cropId, { cropData, traysNeeded }] of result) {
    result.set(cropId, { cropData, traysNeeded: utils.round(traysNeeded, 2) });
  }

  return result;
}

interface PlantingInput {
  readonly traysRounded: number;
  readonly cropData: CropData;
}

interface CropData {
  readonly id: number;
  readonly cropName: string;
}

interface CropDataWithExtra extends CropData {
  readonly totalGrowTime: number;
  readonly harvestBufferTime: number;
  readonly yieldPerTray: number;
  readonly yieldUnit: string;
}

interface PlantingInputInput {
  readonly trays_rounded: string;
  readonly crop?: {
    readonly id: string;
    readonly crop_name: string;
  } | null;
}

export function plantingInputsToCalcInputs(plantings: readonly PlantingInputInput[]): readonly PlantingInput[] {
  return plantings
    .map((planting) => ({
      traysRounded: Number.parseFloat(planting.trays_rounded),
      cropData: {
        id: getNumericID(planting.crop?.id ?? ''),
        cropName: planting.crop?.crop_name ?? '',
      },
    }))
    .filter(utils.notNull);
}

export function calculateAmountsAvailableFromPlantings(
  plantings: readonly PlantingInput[],
): Map<number, { readonly cropData: CropData; readonly traysAvailable: number }> {
  const result = new Map<number, { readonly cropData: CropData; readonly traysAvailable: number }>();
  for (const planting of plantings) {
    const maybeExisting = result.get(planting.cropData.id);
    result.set(planting.cropData.id, {
      cropData: planting.cropData,
      traysAvailable: utils.addTraysRaw(maybeExisting ? maybeExisting.traysAvailable : 0, planting.traysRounded),
    });
  }

  return result;
}

export function getDeficitsFromAmountsNeededAndAvailable(
  amountsNeeded: Map<number, { readonly cropData: CropData; readonly traysNeeded: number }>,
  amountsAvailable: Map<number, { readonly cropData: CropData; readonly traysAvailable: number }>,
): Map<
  number,
  {
    readonly cropData: CropData;
    readonly traysNeeded: number;
    readonly traysAvailable: number;
    readonly traysDeficit: number;
  }
> {
  const result = new Map<
    number,
    {
      readonly cropData: CropData;
      readonly traysNeeded: number;
      readonly traysAvailable: number;
      readonly traysDeficit: number;
    }
  >();
  for (const [cropId, { cropData, traysNeeded }] of amountsNeeded) {
    const maybeAvailable = amountsAvailable.get(cropId);
    const traysAvailable = maybeAvailable?.traysAvailable ?? 0;
    // Positive traysDeficit means there is a deficit
    // Negative traysDeficit means there is a surplus
    const traysDeficit = utils.subtractTraysRaw(traysNeeded, traysAvailable);
    result.set(cropId, {
      cropData,
      traysNeeded,
      traysAvailable,
      traysDeficit,
    });
  }
  // Go through amountsAvailable. If any crop is not in the result map, add it with a surplus
  for (const [cropId, { cropData, traysAvailable }] of amountsAvailable) {
    if (!result.has(cropId)) {
      result.set(cropId, {
        cropData,
        traysNeeded: 0,
        traysAvailable,
        traysDeficit: -1 * traysAvailable,
      });
    }
  }

  return result;
}
